/**
 * Material-UI Theme Configuration
 * Zentrale Theme-Konfiguration für die gesamte App
 */

import { createTheme } from '@mui/material/styles';

// Base theme configuration
const baseTheme = {
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      letterSpacing: '-0.005em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 6,
  },
  spacing: 8,
};

// Light theme
export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#0f172a', // Charcoal/Slate 900
      light: '#334155',
      dark: '#020617',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#475569', // Muted Slate 600
      light: '#64748b',
      dark: '#1e293b',
      contrastText: '#ffffff',
    },
    success: {
      main: '#0f766e', // Emerald/Teal 700
      light: '#14b8a6',
      dark: '#115e59',
      contrastText: '#ffffff',
    },
    error: {
      main: '#be123c', // Deep Red 700
      light: '#f43f5e',
      dark: '#9f1239',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#d97706', // Muted Amber 700
      light: '#f59e0b',
      dark: '#b45309',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2563eb', // Clean Blue 600 for interactive elements
      light: '#3b82f6',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Slate 50
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
      disabled: '#94a3b8',
    },
    divider: '#e2e8f0', // Slate 200
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          padding: '8px 16px',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
          '&:active': {
            transform: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: '#0f172a',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e293b',
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: '#f1f5f9',
          color: '#0f172a',
          border: '1px solid #e2e8f0',
          '&:hover': {
            backgroundColor: '#e2e8f0',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#e2e8f0',
          color: '#475569',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#cbd5e1',
            backgroundColor: '#f8fafc',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
          backgroundImage: 'none',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          '&:hover': {
            borderColor: '#cbd5e1',
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: '#ffffff',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#cbd5e1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2563eb', // Blue ring on focus
              borderWidth: '1.5px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          '&:hover': {
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
        },
        elevation2: {
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
        },
        elevation3: {
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#0f172a',
          boxShadow: 'none',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e2e8f0',
        },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#f8fafc', // Clean white-slate
      light: '#ffffff',
      dark: '#cbd5e1',
      contrastText: '#0f172a',
    },
    secondary: {
      main: '#94a3b8',
      light: '#cbd5e1',
      dark: '#64748b',
      contrastText: '#0f172a',
    },
    success: {
      main: '#0d9488',
      light: '#2dd4bf',
      dark: '#0f766e',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e11d48',
      light: '#fb7185',
      dark: '#be123c',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#d97706',
      light: '#fbbf24',
      dark: '#b45309',
      contrastText: '#ffffff',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0b0f19', // Deep dark blue-black
      paper: '#111827', // Slate 950
    },
    text: {
      primary: '#f8fafc',
      secondary: '#94a3b8',
      disabled: '#4b5563',
    },
    divider: '#1f2937', // Slate 800
    grey: {
      50: '#090d16',
      100: '#0b0f19',
      200: '#1f2937',
      300: '#374151',
      400: '#4b5563',
      500: '#6b7280',
      600: '#9ca3af',
      700: '#d1d5db',
      800: '#e5e7eb',
      900: '#f9fafb',
    },
  },
  components: {
    ...lightTheme.components,
    MuiButton: {
      styleOverrides: {
        ...lightTheme.components.MuiButton.styleOverrides,
        containedPrimary: {
          backgroundColor: '#f8fafc',
          color: '#0f172a',
          '&:hover': {
            backgroundColor: '#cbd5e1',
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: '#1f2937',
          color: '#f8fafc',
          border: '1px solid #374151',
          '&:hover': {
            backgroundColor: '#374151',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#374151',
          color: '#94a3b8',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#4b5563',
            backgroundColor: '#111827',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          border: '1px solid #1f2937',
          backgroundImage: 'none',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          '&:hover': {
            borderColor: '#374151',
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: '#111827',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1f2937',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#374151',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
              borderWidth: '1.5px',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#111827',
          color: '#f8fafc',
          boxShadow: 'none',
          borderBottom: '1px solid #1f2937',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: '1px solid #1f2937',
          backgroundColor: '#111827',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: 'none',
          border: '1px solid #1f2937',
        },
        elevation2: {
          boxShadow: 'none',
          border: '1px solid #1f2937',
        },
        elevation3: {
          boxShadow: 'none',
          border: '1px solid #1f2937',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
          border: '1px solid #1f2937',
        },
      },
    },
  },
});

// Theme creator function
export const createHelpBookTheme = (mode = 'light', primaryColor = 'default') => {
  const baseThemeConfig = mode === 'light' ? lightTheme : darkTheme;
  
  if (primaryColor === 'default' || primaryColor === '#2166C1') {
    return baseThemeConfig;
  }
  
  // If primaryColor is passed and is different from default, map it dynamically but keep it clean
  return createTheme({
    ...baseThemeConfig,
    palette: {
      ...baseThemeConfig.palette,
      primary: {
        main: primaryColor,
        light: lightenColor(primaryColor, 15),
        dark: darkenColor(primaryColor, 15),
        contrastText: getContrastText(primaryColor),
      },
    },
  });
};

// Color utilities
const lightenColor = (color, amount) => {
  if (!color.startsWith('#')) return color;
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return `#${  (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)}`;
};

const darkenColor = (color, amount) => {
  if (!color.startsWith('#')) return color;
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return `#${  (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
    (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
    (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1)}`;
};

const getContrastText = (color) => {
  if (!color.startsWith('#')) return '#ffffff';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#0f172a' : '#ffffff';
};

export default {
  lightTheme,
  darkTheme,
  createHelpBookTheme
};;
